import type { EditorField } from '../@types/editor'

export type QuickPickAnimationPlan = {
  duration: number
  slots: QuickPickAnimationSlot[]
}

export type QuickPickAnimationSlot = QuickPickAnimationTarget & {
  lockTime: number
}

export type QuickPickAnimationTarget = {
  fieldName: string
  value: number
}

export type QuickPickAnimationTiming = {
  lockStagger: number
  minLockTime: number
  tick: number
  total: number
}

export const getQuickPickRollLockTimes = (
  count: number,
  { lockStagger, minLockTime, total }: QuickPickAnimationTiming,
): number[] =>
  Array.from({ length: count }, (_, index) =>
    Math.max(total - count * lockStagger + (index + 1) * lockStagger, minLockTime),
  )

export const getQuickPickScatterLockTimes = (
  targets: QuickPickAnimationTarget[],
  { lockStagger, total }: QuickPickAnimationTiming,
): number[] => {
  const perField = new Map<string, number>()

  return targets.map(({ fieldName }) => {
    const index = perField.get(fieldName) ?? 0
    perField.set(fieldName, index + 1)
    return total + index * lockStagger
  })
}

export const buildQuickPickAnimationPlan = (
  snapshot: EditorField[],
  result: EditorField[],
  toLockTimes: (targets: QuickPickAnimationTarget[]) => number[],
): QuickPickAnimationPlan => {
  const added = result.flatMap((field) => {
    const previous = snapshot.find(({ name }) => name === field.name)?.selected ?? []
    return field.selected
      .filter((value) => !previous.includes(value))
      .map((value) => ({ fieldName: field.name, value }))
  })

  const lockTimes = toLockTimes(added)

  return {
    duration: Math.max(0, ...lockTimes),
    slots: added
      .map((target, index) => ({ ...target, lockTime: lockTimes[index] }))
      .sort((first, second) => first.lockTime - second.lockTime),
  }
}

export const pickQuickPickRollValues = (
  selectableNumbers: number[],
  excluded: number[],
  count: number,
): number[] => {
  const taken = new Set(excluded)
  const pool = selectableNumbers.filter((value) => !taken.has(value))
  const targetCount = Math.min(count, pool.length)
  const values: number[] = []

  while (values.length < targetCount) {
    const index = Math.floor(Math.random() * pool.length)
    values.push(...pool.splice(index, 1))
  }

  return values
}
